import { SessionListDTO } from '../../../store/sessionSlice';
import { matchesSessionSearch } from '../../../utils/sessionDisplay';
import { MERGE_SELECTION_SIZE } from '../../../constants';
import { isSessionArchived, isSessionDeletable } from '../../../utils/sessionMergeState';

export const SESSIONS_PER_PAGE = 50;

export type SessionDateRange = [string, string];

/** The review stages a session can be filtered by, in lifecycle order — one value collapsing
 *  the two independent review axes (validation_state and audit_state). */
export const SESSION_REVIEW_FILTERS = ['none', 'validating', 'validated', 'auditing', 'audited'] as const;
export type SessionReviewFilter = (typeof SESSION_REVIEW_FILTERS)[number];

export const SESSION_REVIEW_FILTER_LABELS: Record<SessionReviewFilter, string> = {
  none: 'Not validated',
  validating: 'Validation in progress',
  validated: 'Validated — ready to audit',
  auditing: 'Audit in progress',
  audited: 'Audited',
};

/** Whose work a session list shows. */
export const SESSION_REVIEW_ROLES = ['reviewer', 'auditor'] as const;
export type SessionReviewRole = (typeof SESSION_REVIEW_ROLES)[number];

/** Filter options per role. 'validated' is in both — the reviewer's finish line, the auditor's
 *  inbox. */
export const SESSION_REVIEW_FILTERS_BY_ROLE: Record<
  SessionReviewRole,
  readonly SessionReviewFilter[]
> = {
  reviewer: ['none', 'validating', 'validated'],
  auditor: ['validated', 'auditing', 'audited'],
};

/** Only 'validated' differs: the reviewer's next step is reporting, the auditor's is auditing. */
export const SESSION_REVIEW_FILTER_LABELS_BY_ROLE: Record<
  SessionReviewRole,
  Record<SessionReviewFilter, string>
> = {
  reviewer: {
    ...SESSION_REVIEW_FILTER_LABELS,
    validated: 'Validated — ready to report incident',
  },
  auditor: SESSION_REVIEW_FILTER_LABELS,
};

interface SessionListControlsState {
  searchQuery: string;
  dateRange: SessionDateRange;
  selectedSites: string[];
  selectedReviewStates: SessionReviewFilter[];
  currentPage: number;
}

type SessionListControlsAction =
  | { type: 'setSearchQuery'; value: string }
  | { type: 'setDateRange'; value: SessionDateRange }
  | { type: 'setSelectedSites'; value: string[] }
  | { type: 'setSelectedReviewStates'; value: SessionReviewFilter[] }
  | { type: 'setCurrentPage'; value: number }
  | { type: 'reset' };

interface SessionListFilters {
  searchQuery: string;
  dateRange: SessionDateRange;
  selectedSites: string[];
  selectedReviewStates: SessionReviewFilter[];
}

interface PageRange {
  start: number;
  end: number;
}

interface PaginationModel<T> {
  items: T[];
  currentPage: number;
  numberOfPages: number;
  range: PageRange;
  totalItems: number;
  title: string;
}

export const initialSessionListControlsState: SessionListControlsState = {
  searchQuery: '',
  dateRange: ['', ''],
  selectedSites: [],
  selectedReviewStates: [],
  currentPage: 1,
};

export const sessionListControlsReducer = (
  state: SessionListControlsState,
  action: SessionListControlsAction,
): SessionListControlsState => {
  switch (action.type) {
    case 'setSearchQuery':
      return { ...state, searchQuery: action.value, currentPage: 1 };
    case 'setDateRange':
      return { ...state, dateRange: action.value, currentPage: 1 };
    case 'setSelectedSites':
      return { ...state, selectedSites: action.value, currentPage: 1 };
    case 'setSelectedReviewStates':
      return { ...state, selectedReviewStates: action.value, currentPage: 1 };
    case 'setCurrentPage':
      return { ...state, currentPage: action.value };
    case 'reset':
      return initialSessionListControlsState;
  }
};

const valueToLocalDateBoundary =
  (value: string) =>
  (boundary: 'start' | 'end'): number | null => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match) {
      return null;
    }

    const [, year, month, day] = match;
    const date = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      boundary === 'start' ? 0 : 23,
      boundary === 'start' ? 0 : 59,
      boundary === 'start' ? 0 : 59,
      boundary === 'start' ? 0 : 999,
    );

    return date.getTime();
  };

const matchesDateRange = (
  createdAt: string,
  [startDate, endDate]: SessionDateRange,
): boolean => {
  if (!startDate && !endDate) {
    return true;
  }

  const sessionTime = new Date(createdAt).getTime();
  if (Number.isNaN(sessionTime)) {
    return false;
  }

  const startTime = startDate
    ? valueToLocalDateBoundary(startDate)('start')
    : null;
  const endTime = endDate
    ? valueToLocalDateBoundary(endDate)('end')
    : null;

  if (startDate && (startTime === null || sessionTime < startTime)) {
    return false;
  }
  if (endDate && (endTime === null || sessionTime > endTime)) {
    return false;
  }

  return true;
};

/** The furthest-along stage: audit_state wins once set, then validation_state, else 'none'. */
export const sessionToReviewFilter = (session: SessionListDTO): SessionReviewFilter =>
  session.audit_state ?? session.validation_state ?? 'none';

export const filterSessions = (
  sessions: SessionListDTO[],
  filters: SessionListFilters,
): SessionListDTO[] =>
  sessions.filter(
    (session) =>
      matchesSessionSearch(session, filters.searchQuery) &&
      (
        filters.selectedSites.length === 0 ||
        !!session.group_id && filters.selectedSites.includes(session.group_id)
      ) &&
      (
        filters.selectedReviewStates.length === 0 ||
        filters.selectedReviewStates.includes(sessionToReviewFilter(session))
      ) &&
      matchesDateRange(session.created_at, filters.dateRange),
  );

export const sortSessionsNewestFirst = (
  sessions: SessionListDTO[],
): SessionListDTO[] =>
  [...sessions].sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );

export const sessionsToAvailableSites = (
  sessions: SessionListDTO[],
): string[] =>
  Array.from(
    new Set(
      sessions
        .map((session) => session.group_id)
        .filter((site): site is string => !!site),
    ),
  ).sort();

export const hasActiveSessionFilters = (
  filters: SessionListFilters,
): boolean =>
  !!(
    filters.searchQuery ||
    filters.dateRange[0] ||
    filters.dateRange[1] ||
    filters.selectedSites.length ||
    filters.selectedReviewStates.length
  );

const itemCountToNumberOfPages =
  (itemCount: number) =>
  (pageSize: number): number =>
    Math.max(1, Math.ceil(itemCount / pageSize));

const valuesToCurrentPage =
  (page: number) =>
  (numberOfPages: number): number =>
    Math.min(Math.max(page, 1), numberOfPages);

const valuesToPageRange =
  (itemCount: number) =>
  (currentPage: number) =>
  (pageSize: number): PageRange => {
    if (itemCount === 0) {
      return { start: 0, end: 0 };
    }

    const startIndex = (currentPage - 1) * pageSize;
    return {
      start: startIndex + 1,
      end: Math.min(startIndex + pageSize, itemCount),
    };
  };

const itemsToPageItems =
  <T,>(items: T[]) =>
  (currentPage: number) =>
  (pageSize: number): T[] => {
    const startIndex = (currentPage - 1) * pageSize;
    return items.slice(startIndex, startIndex + pageSize);
  };

export const valuesToPaginationTitle =
  (start: number) =>
  (end: number) =>
  (total: number): string =>
    `Showing ${start}-${end} of ${total} sessions`;

export const itemsToPagination =
  <T,>(items: T[]) =>
  (requestedPage: number) =>
  (pageSize: number): PaginationModel<T> => {
    const numberOfPages = itemCountToNumberOfPages(items.length)(pageSize);
    const currentPage = valuesToCurrentPage(requestedPage)(numberOfPages);
    const range = valuesToPageRange(items.length)(currentPage)(pageSize);

    return {
      items: itemsToPageItems(items)(currentPage)(pageSize),
      currentPage,
      numberOfPages,
      range,
      totalItems: items.length,
      title: valuesToPaginationTitle(range.start)(range.end)(items.length),
    };
  };

// ===== MERGE SELECTION =====

/** What one card's merge checkbox shows, for a given selection. */
export interface SessionMergeSelectionView {
  checked: boolean;
  disabled: boolean;
}

/**
 * Any session can be picked: the compare page explains why a pair can't merge. Only the
 * two-pick limit applies, so once two are picked the rest wait until one is unpicked.
 */
export const selectedIdsToMergeSelectionView =
  (selectedIds: string[]) =>
  (session: SessionListDTO): SessionMergeSelectionView => {
    const checked = selectedIds.includes(session.session_id);
    return { checked, disabled: !checked && selectedIds.length >= MERGE_SELECTION_SIZE };
  };

/** True when nothing about the session itself rules out a merge (merge_blockers is empty). */
export const isMergeableSession = (session: SessionListDTO): boolean =>
  (session.merge_blockers ?? []).length === 0;

/**
 * The sessions to show with "Hide sessions that can't be merged" on. A picked session always
 * stays, so a pick never disappears from the list.
 *
 * This relies only on merge_blockers from the session list, and stays accurate for
 * conversations still being processed: the service reports a live processing job as
 * running_tasks there, and treats a conversation with no live job as stuck rather than
 * blocking (WHSNavigatorService session_merge_utils.transcript_blockers). Keep the two in
 * step. What this can't know are the pair rules (same site, combinable session types,
 * conflicting hand-set values), so the compare page can still refuse a pair.
 */
export const filterMergeableSessions =
  (selectedIds: string[]) =>
  (sessions: SessionListDTO[]): SessionListDTO[] =>
    sessions.filter((session) => isMergeableSession(session) || selectedIds.includes(session.session_id));

export const selectedIdsToCanCompare = (selectedIds: string[]): boolean =>
  selectedIds.length === MERGE_SELECTION_SIZE;

export const selectedIdsToCompareLabel = (selectedIds: string[]): string =>
  `Compare (${selectedIds.length} of ${MERGE_SELECTION_SIZE})`;

export const selectedIdsToComparePath = (selectedIds: string[]): string =>
  `/sessions/merge?ids=${selectedIds.map(encodeURIComponent).join(',')}`;

// ===== MERGE STATE ON THE LIST =====

export interface SessionBadge {
  label: string;
  background: string;
  color: string;
}

const ARCHIVED_BADGE: SessionBadge = { label: 'Archived', background: '#eeeeee', color: '#424242' };

/** Archived sources get a badge; every other session, including a merged one, gets none. */
export const sessionToMergeBadge = (session: SessionListDTO): SessionBadge | null =>
  isSessionArchived(session) ? ARCHIVED_BADGE : null;

/** The sessions Delete All may remove: not recording and not merge-locked. */
export const sessionsToDeletable = (sessions: SessionListDTO[]): SessionListDTO[] =>
  sessions.filter(isSessionDeletable);

export const deleteAllCountsToConfirmation =
  (deletableCount: number) =>
  (skippedCount: number): string =>
    skippedCount > 0
      ? `Delete ${deletableCount} session(s)?\n\n${skippedCount} session(s) that are recording or part of a merge will be skipped.\n\nThis will permanently remove all associated data (audio files, transcripts, analysis results).`
      : `Delete all ${deletableCount} session(s)?\n\nThis will permanently remove all associated data (audio files, transcripts, analysis results).`;
